import {
	CAP_TABLE_EXPAND_ROWS_MODE,
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
	CAP_TABLE_VIEW_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings';
import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils';
import { useDidUpdate } from '@mantine/hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { CapTableRail } from '@dealstack/shared/lib/generated';

interface CapTableLocalStorageSettings {
	valueDisplayMode: CAP_TABLE_VALUE_DISPLAY_MODE;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	tableView: CAP_TABLE_VIEW_TYPE;
	showSummarized: boolean;
	showRowsCheckboxes: boolean;
	expandRowsMode: CAP_TABLE_EXPAND_ROWS_MODE;
	// CAP-1972: beneficial/legal title rail (nominee structures). Beneficial is the default view.
	rail: CapTableRail;
}

export const useCapTablePageController = () => {
	const { dealId, deal, isAllTransactionValidInDeal } = useContext(DealContext);
	const [valueDisplayMode, setValueDisplayMode] = useState(CAP_TABLE_VALUE_DISPLAY_MODE.ABSOLUTE);
	const [valuesHeldType, setValuesHeldType] = useState(CAP_TABLE_VALUES_HELD_TYPE.SECURITIES);
	const [tableView, setTableView] = useState(CAP_TABLE_VIEW_TYPE.VIEW_1);
	const [showSummarized, setShowSummarized] = useState(true);
	const [showRowsCheckboxes, setShowRowsCheckboxes] = useState(false);
	const [expandRowsMode, setExpandRowsMode] = useState(CAP_TABLE_EXPAND_ROWS_MODE.EXPAND_ALL);
	const [isMasterView, setIsMasterView] = useState(false);
	// CAP-1972: beneficial is the default rail; the toggle is only shown on nominee-structure deals.
	const [rail, setRail] = useState(CapTableRail.BENEFICIAL);

	const isFundDeal = deal?.isFund ?? false;

	useEffect(() => {
		const settings = StorageUtilities.getItem<CapTableLocalStorageSettings>(
			StorageKeys.CAP_TABLE_LOCAL_STORAGE_SETTINGS_KEY,
			dealId
		);
		if (!settings) {
			return;
		}

		setValueDisplayMode(settings.valueDisplayMode);
		setValuesHeldType(settings.valuesHeldType);
		setShowSummarized(settings.showSummarized);
		setTableView(settings.tableView);
		setShowRowsCheckboxes(settings.showRowsCheckboxes);
		setExpandRowsMode(settings.expandRowsMode);
		// Older persisted settings predate the rail — fall back to the beneficial default.
		setRail(settings.rail ?? CapTableRail.BENEFICIAL);
	}, []);

	useDidUpdate(() => {
		const settings = {
			valueDisplayMode,
			valuesHeldType,
			tableView,
			showSummarized,
			showRowsCheckboxes,
			expandRowsMode,
			rail,
		};
		StorageUtilities.setItem(
			StorageKeys.CAP_TABLE_LOCAL_STORAGE_SETTINGS_KEY,
			settings,
			dealId
		);
	}, [
		valueDisplayMode,
		valuesHeldType,
		tableView,
		showSummarized,
		showRowsCheckboxes,
		expandRowsMode,
		rail,
	]);

	// hide rows checkboxes when not displaying percentages
	useEffect(() => {
		if (valueDisplayMode === CAP_TABLE_VALUE_DISPLAY_MODE.ABSOLUTE) {
			setShowRowsCheckboxes(false);
		}
	}, [valueDisplayMode]);

	const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

	const tablePageRef = useRef<HTMLDivElement | null>(null);
	const tableWrapperRef = useRef<HTMLTableElement | null>(null);
	const tableRef = useRef<HTMLTableElement | null>(null);

	return {
		dealId,
		valueDisplayMode,
		setValueDisplayMode,
		valuesHeldType,
		setValuesHeldType,
		tableView,
		setTableView,
		showSummarized,
		setShowSummarized,
		tablePageRef,
		tableRef,
		tableWrapperRef,
		isDownloadingExcel,
		setIsDownloadingExcel,
		isDownloadingPdf,
		setIsDownloadingPdf,
		showPercentages: valueDisplayMode === CAP_TABLE_VALUE_DISPLAY_MODE.PERCENTAGE,
		showRowsCheckboxes,
		setShowRowsCheckboxes,
		isAllTransactionValidInDeal,
		expandRowsMode,
		setExpandRowsMode,
		isFundDeal,
		isMasterView,
		setIsMasterView,
		rail,
		setRail,
	};
};
